/**
 * File sharing: uploads go to R2 through the Worker (no storage credentials on clients), downloads through an
 * auth-checked Worker route.
 *
 * Upload flow: createUpload → (optional) PUT thumb → PUT content (single request, up to ~95 MB) or, for bigger files,
 * PUT parts/:n (R2 multipart, `upload.partSize` each) + POST complete → the file is `ready` → post a message with
 * `fileIds` (PostMessageRequest), or reference it from a page block (`pageId` uploads are attached on completion).
 * Uploads that never get attached are deleted after 24 h.
 *
 * Reading: `FileRef.url` / `thumbUrl` are API-relative paths. Clients that can set headers send the bearer token;
 * where they can't (img/video src, download links) they append `?t=<FileTokenResponse.token>`.
 */
import { z } from "zod";
import { ChannelKind, FileRef, Id, Timestamp } from "./entities.ts";

/** `pending`: created, bytes not (all) stored yet; `ready`: stored and shareable. */
export const UploadStatus = z.enum(["pending", "ready"]);
export type UploadStatus = z.infer<typeof UploadStatus>;

export const CreateUploadRequest = z.object({
  /** File name as picked (path components are stripped server-side), 1–255 characters. */
  name: z.string().min(1).max(255),
  /** Exact byte size, at most 1 GiB (5 TB for purpose "drive", drive.ts DRIVE_MAX_UPLOAD_BYTES). The content (or the parts) must add up to it. */
  size: z.number().int().nonnegative().max(5_000_000_000_000),
  /** MIME type as the client knows it; empty or absent ⇒ derived from the name. */
  mime: z.string().max(255).optional(),
  /** Conversation the file will be shared in (the poster must be a member). One of channelId / pageId. */
  channelId: Id.optional(),
  /** Page the file is embedded in (docs image/file blocks; the uploader needs edit access). */
  pageId: Id.optional(),
  /**
   * "sticker": a sticker image (neither channelId nor pageId). It counts as attached once a sticker uses it
   * (stickers.ts `CreateStickerRequest.fileId`); every workspace member may view it from then on.
   * "avatar" / "workspace_icon": a profile photo or workspace icon (WebP/PNG/JPEG ≤ 2 MB, workspace icons by admins
   * only), attached by settings.ts `UpdateProfileRequest.avatarFileId` / `UpdateWorkspaceRequest.iconFileId`; every
   * member may view it from then on.
   * "import": a Slack or Notion export .zip (owners and admins), attached by imports.ts `CreateImportRequest.fileId`;
   * only the import reads it, and it is deleted when the import ends.
   * "huddle": a recording of a link huddle (it has no conversation), attached by huddles.ts `PostRecordingRequest`;
   * only the people who were in the huddle may play it.
   * "drive": a file or a new version in Files (drive.ts), attached by `CreateDriveItemRequest.fileId` /
   * `AddDriveVersionRequest.fileId`. With `sha256`, content the workspace already stores is reused: the response is
   * `ready` at once and nothing is uploaded.
   */
  purpose: z.enum(["sticker", "avatar", "workspace_icon", "import", "mail", "huddle", "drive"]).optional(),
  /** Pixel size of images and videos (measured on the client). */
  width: z.number().int().positive().max(100_000).optional(),
  height: z.number().int().positive().max(100_000).optional(),
  /** Hex SHA-256 of the content, when the client computed it (stored as metadata, not verified). */
  sha256: z.string().regex(/^[0-9a-f]{64}$/).optional(),
  /** Retry-safe: the same clientId from the same person returns the same upload. */
  clientId: z.string().max(64).optional(),
});
export type CreateUploadRequest = z.infer<typeof CreateUploadRequest>;

/** How to send the bytes. */
export const UploadPlan = z.object({
  /**
   * "single": PUT the whole body to /content. "multipart": PUT each part to /parts/:n (1-based), then POST /complete.
   * "direct" (Files uploads whose parts are bigger than a Worker request body, about 1 TB and up): for each part, POST
   * /parts/:n/url and PUT the part's bytes to the returned presigned R2 URL (no auth header), then POST /complete.
   */
  mode: z.enum(["single", "multipart", "direct"]),
  /** Multipart / direct: every part except the last has exactly this many bytes. */
  partSize: z.number().int().optional(),
  partCount: z.number().int().optional(),
  /** Multipart / direct: parts the server already stored (resume after a reload or a dropped connection). */
  uploadedParts: z.array(z.number().int()).optional(),
});
export type UploadPlan = z.infer<typeof UploadPlan>;

export const CreateUploadResponse = z.object({ file: FileRef, status: UploadStatus, upload: UploadPlan });
export type CreateUploadResponse = z.infer<typeof CreateUploadResponse>;

/** The stored file (content PUT, multipart completion). */
export const FileResponse = z.object({ file: FileRef });
export type FileResponse = z.infer<typeof FileResponse>;

export const UploadPartResponse = z.object({ partNumber: z.number().int() });
export type UploadPartResponse = z.infer<typeof UploadPartResponse>;

/** Direct uploads: where to PUT one part (straight to R2; the URL must be used before `expiresAt`). */
export const UploadPartUrlResponse = z.object({ partNumber: z.number().int(), url: z.string(), expiresAt: Timestamp });
export type UploadPartUrlResponse = z.infer<typeof UploadPartUrlResponse>;

/**
 * Read token for file URLs (`?t=`): bound to the caller and the workspace, valid for 12–24 h, good only for GETs of
 * file content and thumbnails. Permissions are re-checked on every request, so leaving a channel revokes access at
 * once. Tokens issued in the same 12 h window are identical, so file URLs (and the HTTP cache) stay stable.
 */
export const FileTokenResponse = z.object({ token: z.string(), expiresAt: Timestamp });
export type FileTokenResponse = z.infer<typeof FileTokenResponse>;

/** Who can open a file: the file page's permissions panel. */
export const FileAudience = z.object({
  /**
   * "channel"/"dm": shared in a conversation; "page": embedded in a page; "private": not shared yet (uploader only);
   * "workspace": a sticker image (every member of the workspace).
   */
  kind: z.enum(["channel", "dm", "page", "private", "workspace"]),
  channelId: Id.optional(),
  /** Channel name (channels only; DMs derive their name from `userIds`). */
  channelName: z.string().optional(),
  channelKind: ChannelKind.optional(),
  /** People in the conversation. */
  memberCount: z.number().int().optional(),
  /** Restricted members (guests and agents) among them. */
  guestCount: z.number().int().optional(),
  /** …of whom this many can view images inline but can't download originals. */
  guestsWithoutDownload: z.number().int().optional(),
  /** DM / group DM participants. */
  userIds: z.array(Id).optional(),
  pageId: Id.optional(),
  pageTitle: z.string().optional(),
});
export type FileAudience = z.infer<typeof FileAudience>;

export const FileAccessReason = z.enum([
  /** The upload hasn't finished (only its uploader sees it). */
  "pending",
  /** The caller isn't in the conversation it was shared in (`canJoin`: they may join to get access). */
  "not_in_channel",
  /** Restricted access without `can.downloadFiles`: images inline only, no originals. */
  "no_download_permission",
  /** The type is never shown inline (executables, HTML, SVG, scripts…): download only. */
  "preview_blocked",
  /** The caller's guest/agent access has expired. */
  "access_expired",
]);
export type FileAccessReason = z.infer<typeof FileAccessReason>;

/** The caller's effective permissions on one file (core `logic/files#fileAccess`, shared by API and clients). */
export const FilePermissions = z.object({
  /** May see the file's details and open its page. */
  canView: z.boolean(),
  /** The preview may be rendered inline (type policy + guest rules). */
  canPreview: z.boolean(),
  /** May download the original. */
  canDownload: z.boolean(),
  /** May join the conversation it was shared in. */
  canJoin: z.boolean(),
  /** Why something above is false (empty when everything is allowed). */
  reasons: z.array(FileAccessReason),
});
export type FilePermissions = z.infer<typeof FilePermissions>;

export const FileInfoResponse = z.object({
  file: FileRef,
  status: UploadStatus,
  uploaderId: Id,
  createdAt: Timestamp,
  /** The message it was shared in (absent for page files and unshared uploads). */
  messageId: Id.optional(),
  /** Set when that message is a thread reply. */
  threadRootId: Id.optional(),
  /** When it was shared (the message's time). */
  sharedAt: Timestamp.optional(),
  audience: FileAudience,
  permissions: FilePermissions,
});
export type FileInfoResponse = z.infer<typeof FileInfoResponse>;
